import React, { ChangeEvent, FormEvent, useEffect } from 'react'
import {
  Button,
  VStack,
  HStack,
  ModalFooter,
  FormLabel,
  FormControl,
} from '@chakra-ui/react'
import TextInput from '../../../shared/ui/text-input/TextInput'
import AvatarInput from '../../../shared/ui/avatar-input/AvatarInput'
import useTeam from '../../../../hooks/useTeam'

import './team.form.scss'
import ChakraReactSelect from './chakra.react.select'
import { gameOptions } from '../../../../helpers/constants'

export interface CreateTeamFormValues {
  id: string
  name: string
  avatar: string
  description: string
  game: string
}

interface CreateTeamFormProps {
  values: CreateTeamFormValues
  handleSubmit: (event: FormEvent<HTMLFormElement>) => void
  errors: CreateTeamFormValues
  touched: {
    name: boolean
    description: boolean
    game: boolean
    avatar: boolean
  }
  isValid: boolean
  handleChange: (
    event: FormEvent<HTMLParagraphElement> | ChangeEvent<HTMLSelectElement>
  ) => void
  handleBlur: (event: FormEvent<HTMLParagraphElement>) => void
  handleReset: () => void
  dirty: boolean
  isSubmitting: boolean
  setFieldValue: (key: string, value: string) => void
  readonly?: boolean
  validateForm: () => void
  isEditing?: boolean
  handleCloseModal?: () => void
  isTeamCreating?: boolean
}

const CreateTeamForm: React.FC<CreateTeamFormProps> = ({
  values = {
    name: '',
    description: '',
    game: '',
    avatar: '',
    id: '',
  },
  errors,
  touched = {},
  isValid = false,
  isSubmitting = false,
  handleSubmit,
  handleChange,
  handleBlur,
  handleReset,
  setFieldValue,
  validateForm,
  isEditing = false,
  handleCloseModal,
  isTeamCreating = false,
}) => {
  const { name, description, avatar, game } = values
  const { uploadAvatarToS3, uploading } = useTeam()

  useEffect(() => {
    validateForm()
  }, [validateForm])

  const selectChange = (event: any) => {
    setFieldValue('game', event?.value)
  }
  return (
    <form
      onSubmit={handleSubmit}
      onReset={handleReset}
      autoComplete="off"
      className="team-form"
    >
      <VStack justifyContent="flex-start">
        <AvatarInput
          className="team-form__avatar_container"
          name="avatar"
          id="avatar"
          value={avatar}
          onChange={async (file: File | null) => {
            if (file) {
              const fileUrl = await uploadAvatarToS3(file)
              if (fileUrl) {
                setFieldValue('avatar', fileUrl)
              }
            } else {
              setFieldValue('avatar', '')
            }
          }}
          placeholder={avatar}
          error={Boolean(errors.avatar) && Boolean(touched.avatar)}
          // helperText="Avatar upload is currently unavailable. Please try again later."
        />

        <TextInput
          name="name"
          id="name"
          label="Team Name"
          value={name || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={name}
          error={Boolean(errors.name) && Boolean(touched.name)}
          helperText={
            Boolean(errors.name) && Boolean(touched.name) ? errors.name : ''
          }
        />
        <TextInput
          name="description"
          label="Description"
          id="description"
          value={description || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={description}
          error={Boolean(errors.description) && Boolean(touched.description)}
          helperText={
            Boolean(errors.description) && Boolean(touched.description)
              ? errors.description
              : ''
          }
        />
        <FormControl id="game">
          <FormLabel>Game</FormLabel>

          <ChakraReactSelect
            className="team-form__game-select"
            selectChange={selectChange}
            placeholder={game || 'Select a game'}
            options={gameOptions}
            isDisabled={false}
          />
        </FormControl>
      </VStack>
      <ModalFooter
        maxW="300px"
        w="300px"
        margin="0 auto"
        paddingInlineStart={0}
        paddingInlineEnd={0}
        justifyContent="center"
        mt={2}
        mb={2}
      >
        <HStack w="100%" justifyContent="center">
          <Button
            lineHeight={1}
            color="white"
            type="button"
            className="outline"
            variant="outline"
            borderRadius="4px"
            onClick={handleCloseModal}
            isFullWidth
          >
            Cancel
          </Button>
          <Button
            lineHeight={1}
            type="submit"
            color="white"
            borderRadius="4px"
            variant="solid"
            isFullWidth
            bg="brand.400"
            _hover={{
              bg: 'brand.500',
            }}
            isLoading={uploading || isTeamCreating}
            disabled={isSubmitting || !isValid}
          >
            {isEditing ? 'Update Team' : 'Create Team'}
          </Button>
        </HStack>
      </ModalFooter>
    </form>
  )
}

export default CreateTeamForm
